import React, { useState, useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { GuildUser, useAuth } from '@guildeducationinc/guild-auth';
import { useMediaQuery } from 'react-responsive';
import classnames from 'classnames';
import { HashRouter, Link, Route, Switch, useLocation } from 'react-router-dom';
import GuildAccountPictogram from '@guildeducationinc/recess/pictograms/GuildAccountPictogram';
import {
  Alert,
  DefaultTheme,
  Loading,
  recessColors,
  Spacer,
  Stepper,
  Typography,
} from '@guildeducationinc/recess';
import { clickable } from '@src/analytics';
import Divider from '@src/components/Shared/Divider/Divider';
import BasicInformation from '@pages/EditProfile/forms/BasicInformation';
import ContactInformation from '@pages/EditProfile/forms/ContactInformation';
import ChangePassword from '@pages/EditProfile/forms/ChangePassword';
import EmploymentInformation from '@src/components/Pages/EditProfile/forms/EmploymentInformation';
import ProgramCompletionDates from '@pages/EditProfile/forms/ProgramCompletionDates';
import {
  MOBILE_STEPPER_QUERY,
  SALESFORCE_NEW_REQUEST_URL,
  PROGRAM_COMPLETION_DATES_PATH,
} from '@src/constants';
import { CommunicationSettingsWrapper } from '@src/components/Pages/EditProfile/forms/CommunicationSettingsWrapper';
import { useMemberTraitsContext } from '@src/context/MemberTraitsContext';
import useEmployerService from '@src/hooks/useEmployerService';
import FeedbackWidget from '@src/remotes/FeedbackWidget/FeedbackWidget';
import Skeleton from '@src/components/Shared/Skeleton/Skeleton';
import Referral from './Refer/Referral/Referral';
import './EditProfile.scss';

const EditProfile: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_STEPPER_QUERY);
  const [currentSectionLabel, setCurrentSectionLabel] = useState('Basic information');
  const [formData, setFormData] = useState<GuildUser>();
  const [showDeleteProgramError, setShowDeleteProgramError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showEmailSent, setShowEmailSent] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showMaxEnrollmentsError, setShowMaxEnrollmentsError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCommsSuccess, setShowCommsSuccess] = useState(false);
  const [showCommsError, setShowCommsError] = useState(false);
  const { memberTraits } = useMemberTraitsContext();
  const { employer } = useEmployerService(user?.employerId);
  const displayReferLink = memberTraits?.hasReferrals;
  const heroRef = useRef<HTMLDivElement>(null);
  const referralTitle =
    employer?.name === 'Walmart' ? 'Refer a family member' : 'Refer a colleague';

  useEffect(() => {
    if (user) {
      setFormData({ ...user });
      // let DataDog know this is 'usable' when the guild-auth user is loaded
      datadogRum.addTiming('edit_profile_page');
    }
  }, [user]);

  useEffect(() => {
    const sectionLabels = {
      '#basic-information': 'Basic information',
      '#contact-information': 'Contact information',
      '#communication-settings': 'Communication settings',
      '#employment-information': 'Employment information',
      '#program-completion-dates': 'Program completion dates',
      '#refer': referralTitle,
      '#change-password': 'Change password',
    };

    if (location.hash) {
      setCurrentSectionLabel(sectionLabels[location.hash]);
    }
  }, [location.hash, referralTitle]);

  useEffect(() => {
    window.addEventListener('hashchange', hideAlerts, false);

    return () => {
      window.removeEventListener('hashchange', hideAlerts);
    };
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCommsSuccess(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showCommsSuccess]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEmailSent(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [showEmailSent]);

  useEffect(() => {
    if (
      showError ||
      showMaxEnrollmentsError ||
      showDeleteProgramError ||
      showSuccess ||
      showEmailSent ||
      showEmailError ||
      showCommsSuccess ||
      showCommsError
    ) {
      heroRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    showError,
    showMaxEnrollmentsError,
    showDeleteProgramError,
    showSuccess,
    showEmailSent,
    showEmailError,
    showCommsSuccess,
    showCommsError,
  ]);

  const accountContentClassNames = classnames('accountContent', ...notMobileClassNames('flex'));

  const accountSectionClassNames = classnames(
    'accountSection',
    'flex--1',
    'padding--y_24',
    ...mobileClassNames('margin--top_24', 'padding--x_16'),
    ...notMobileClassNames('padding--left_56')
  );

  const accountSubHeaderClassNames = classnames(
    'subHeader',
    ...mobileClassNames('padding--top_8', 'padding--bottom_4')
  );

  const guildAccountPictogramClassNames = classnames(
    'guildAccountPictogram',
    'margin--right_8',
    ...notMobileClassNames('margin--bottom_8')
  );

  const accountHeroClassNames = classnames(
    'accountHero',
    'position--relative',
    ...mobileClassNames('padding--top_48', 'padding--bottom_12'),
    ...notMobileClassNames('padding--top_64', 'padding--bottom_24')
  );

  function mobileClassNames(...args: String[]): String[] {
    return isMobile ? args : [];
  }

  function notMobileClassNames(...args: String[]): String[] {
    return !isMobile ? args : [];
  }

  const handleStepperClick = (
    event: React.MouseEvent<HTMLElement> | React.FormEvent<HTMLElement>
  ) => {
    return clickable({
      product: 'account',
      loggedIn: true,
      tag: 'account_profile',
      text: event.currentTarget.getAttribute('data-text') || '',
      type: 'sidenav',
    });
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleCloseMaxEnrollmentsError = () => {
    setShowMaxEnrollmentsError(false);
  };
  const handleCloseShowEmail = () => {
    setShowEmailSent(false);
  };
  const handleCloseShowEmailError = () => {
    setShowEmailError(false);
  };
  const handleCloseShowCommsSuccess = () => {
    setShowCommsSuccess(false);
  };
  const handleCloseShowCommsError = () => {
    setShowCommsError(false);
  };
  const handleCloseDeleteProgramError = () => {
    setShowDeleteProgramError(false);
  };

  const redirectToHelpCenter = () => {
    window.open(SALESFORCE_NEW_REQUEST_URL, '_blank', 'noopener,noreferrer');
  };

  const hideAlerts = () => {
    setShowSuccess(false);
    setShowError(false);
    setShowEmailSent(false);
    setShowEmailError(false);
    setShowCommsSuccess(false);
    setShowCommsError(false);
  };

  return (
    <>
      <div className={accountHeroClassNames} ref={heroRef}>
        <div className='accountHeroContent'>
          <Alert
            type='danger'
            display={showError}
            handleClose={handleCloseError}
            className='accountSaveAlert'
            testid='account-save-error'
          >
            <Alert.Content>
              <span>
                There was an error saving your information. Try again or report a problem.
              </span>
            </Alert.Content>
            <Alert.Action onClick={() => redirectToHelpCenter()}>Report a problem</Alert.Action>
          </Alert>

          <Alert
            type='danger'
            display={showCommsError}
            handleClose={handleCloseShowCommsError}
            className='accountSaveAlert'
            testid='comms-save-error'
          >
            <Alert.Content>
              <span>There was an error when saving your changes. Please try again.</span>
            </Alert.Content>
            <Alert.Action onClick={() => redirectToHelpCenter()}>Report a problem</Alert.Action>
          </Alert>

          <Alert
            type='danger'
            display={showDeleteProgramError}
            handleClose={handleCloseDeleteProgramError}
            className='accountSaveAlert'
            testid='account-save-error'
          >
            <Alert.Content>
              <span>There was an error deleting your record. Try again or report a problem.</span>
            </Alert.Content>
            <Alert.Action onClick={() => redirectToHelpCenter()}>Report a problem</Alert.Action>
          </Alert>

          <Alert
            type='danger'
            display={showMaxEnrollmentsError}
            handleClose={handleCloseMaxEnrollmentsError}
            className='accountSaveAlert'
            testid='account-save-error'
          >
            <Alert.Content>
              <span>You have reached the maximum number of enrollments allowed.</span>
            </Alert.Content>
            <Alert.Action onClick={() => redirectToHelpCenter()}>Contact support</Alert.Action>
          </Alert>

          <Alert
            type='success'
            display={showSuccess}
            className='accountSaveAlert'
            testid='account-save-success'
            hideClose={true}
          >
            Profile changes saved
          </Alert>

          <Alert
            type='success'
            display={showEmailSent}
            className='accountSaveAlert'
            testid='sent-email-reset-success'
            handleClose={handleCloseShowEmail}
          >
            <Alert.Content>
              <span>
                Check your email address {formData?.email} for a password reset message. Don&apos;t
                see it? Try checking the spam and junk folders.
              </span>
            </Alert.Content>
          </Alert>

          <Alert
            type='success'
            display={showCommsSuccess}
            className='accountSaveAlert'
            testid='comms-save-success'
            hideClose={true}
            handleClose={handleCloseShowCommsSuccess}
          >
            Your settings have been saved. Changes should take effect in 7-10 days.
          </Alert>

          <Alert
            type='danger'
            display={showEmailError}
            className='accountSaveAlert'
            testid='sent-email-reset-failure'
            handleClose={handleCloseShowEmailError}
          >
            <Alert.Content>
              <span>
                There was an error sending the password reset email. Try again or report a problem.
              </span>
            </Alert.Content>
            <Alert.Action onClick={() => redirectToHelpCenter()}>Report a problem</Alert.Action>
          </Alert>

          <Typography variant='h1' color={recessColors.white.base}>
            Account
          </Typography>
        </div>
      </div>
      <div className='editProfile'>
        <div className='accountHeaderContent padding--y_20'>
          <Typography className='flex flexItems--center' variant='h2'>
            <GuildAccountPictogram
              className={guildAccountPictogramClassNames}
              strokeColor={DefaultTheme.iconColor}
            />{' '}
            Profile
          </Typography>
          <Typography variant='body2' className={accountSubHeaderClassNames}>
            Provide your details so we can keep in contact and follow your progress.
          </Typography>
        </div>
      </div>
      {!isMobile && <Divider />}
      <Skeleton height='20rem'>
        <div className={accountContentClassNames}>
          <HashRouter hashType='noslash'>
            <Stepper className='stepper' mobileButtonLabel={currentSectionLabel}>
              <Stepper.Content className='stepperContent'>
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === '#basic-information' || location.hash === ''}
                  Component={Link}
                  to='basic-information'
                  replace
                  label='Basic information'
                  testid='stepper-link-basic-information'
                  data-text='basic_information'
                />
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === '#contact-information'}
                  Component={Link}
                  to='contact-information'
                  replace
                  label='Contact information'
                  testid='stepper-link-contact-information'
                  data-text='contact_information'
                />
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === '#communication-settings'}
                  Component={Link}
                  to='communication-settings'
                  replace
                  label='Communication settings'
                  testid='stepper-link-communication-settings'
                  data-text='communication_settings'
                />
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === '#employment-information'}
                  Component={Link}
                  to='employment-information'
                  replace
                  label='Employment information'
                  testid='stepper-link-employment-information'
                  data-text='employment_information'
                />
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === `#${PROGRAM_COMPLETION_DATES_PATH}`}
                  Component={Link}
                  to={PROGRAM_COMPLETION_DATES_PATH}
                  replace
                  label='Program completion dates'
                  testid='stepper-link-program-completion-dates'
                  data-text='program_completion_dates'
                />
                {displayReferLink && (
                  <Stepper.Link
                    onClick={(e) => handleStepperClick(e)}
                    active={location.hash === '#refer'}
                    Component={Link}
                    to='refer'
                    replace
                    label={referralTitle}
                    testid='stepper-link-refer'
                    data-text='refer'
                  />
                )}
                <Stepper.Link
                  onClick={(e) => handleStepperClick(e)}
                  active={location.hash === '#change-password'}
                  Component={Link}
                  to='change-password'
                  replace
                  label='Change password'
                  testid='stepper-link-change-password'
                  data-text='change_password'
                />
              </Stepper.Content>
            </Stepper>

            <div className={accountSectionClassNames}>
              <Loading
                label='edit profile'
                loaded={!!user}
                showChildrenWhileLoading={true}
                overlay={true}
              >
                <Switch>
                  <Route exact path={['/', '/basic-information']}>
                    <BasicInformation
                      formData={formData}
                      setFormData={setFormData}
                      setShowError={setShowError}
                      setShowSuccess={setShowSuccess}
                    />
                  </Route>
                  <Route path='/contact-information'>
                    <ContactInformation
                      formData={formData}
                      setFormData={setFormData}
                      setShowError={setShowError}
                      setShowSuccess={setShowSuccess}
                    />
                  </Route>
                  <Route path='/communication-settings'>
                    <CommunicationSettingsWrapper
                      formData={formData}
                      setCommsShowError={setShowCommsError}
                      setCommsShowSuccess={setShowCommsSuccess}
                    />
                  </Route>
                  <Route path='/employment-information'>
                    <EmploymentInformation />
                  </Route>
                  <Route path={`/${PROGRAM_COMPLETION_DATES_PATH}`}>
                    <ProgramCompletionDates
                      setShowDeleteProgramError={setShowDeleteProgramError}
                      setShowError={setShowError}
                      setShowMaxEnrollmentsError={setShowMaxEnrollmentsError}
                      setShowSuccess={setShowSuccess}
                    />
                  </Route>
                  {displayReferLink && (
                    <Route path='/refer'>
                      <Referral employerName={employer?.name} title={referralTitle} />
                    </Route>
                  )}
                  <Route path='/change-password'>
                    <ChangePassword
                      setShowEmailSent={setShowEmailSent}
                      setShowEmailError={setShowEmailError}
                    />
                  </Route>
                </Switch>
              </Loading>
              {user && (
                <Spacer mt={48}>
                  <FeedbackWidget analytics={window.analytics} user={user} />
                </Spacer>
              )}
            </div>
          </HashRouter>
        </div>
      </Skeleton>
    </>
  );
};

export default EditProfile;
